/**
 * lausd-salary-table.js
 * CE Credits Online — LAUSD Salary Table Calculator
 */

// ── SALARY TABLE DATA ──────────────────────────────────────
// Source: LAUSD Office of the Chief HR Officer — 2026-2027 Preparation
// Salary (T) Table (Personnel Policy Guide S3), covering July 1, 2026 -
// December 31, 2026, reflecting the new UTLA/LAUSD agreement ratified
// April 12, 2026.
//
// STATUS: This is CE Credits Online's own good-faith estimate, built from
// LAUSD's published table cross-checked against UTLA's published salary
// information. It is not an official statement from LAUSD or UTLA.
//
// KEY CHANGE FROM PRIOR YEAR: previously only PSG 27 (98 pts) continued
// past Step 10 to Step 14 — PSG 25 and 26 were capped at Step 10. Under
// the new agreement, PSG 25 and 26 also continue through Step 14.
//
// A second-half table (effective Jan 1, 2027) has not yet been published
// by LAUSD as of this build. When it is, replace DATA below and update
// the "effective" date language in lausd-salary-table.liquid.
//
// KNOWN LIMITATION: teachers who were placed on PSG 25, 26, or 27 at
// Step 10 before July 1, 2026 received a one-time placement adjustment
// based on their assignment anniversary date, per UTLA's TA FAQ. This
// calculator computes placement from current years of service and points
// only, and does NOT model that adjustment — a veteran teacher's actual
// current step may differ from what a fresh years-of-service lookup
// shows. Once you know your actual current PSG/Step (from your pay stub
// or UTLA's calculator), the dollar figures below are accurate for that
// placement. See the caveat note in the liquid file's "About the New
// Contract" section.
// ──────────────────────────────────────────────────────────

const ROWS = [
  { psg: 20, pts: 0,  label: 'PSG 20<br>Min (0 pts)' },
  { psg: 21, pts: 14, label: 'PSG 21<br>+14 pts' },
  { psg: 22, pts: 28, label: 'PSG 22<br>+28 pts' },
  { psg: 23, pts: 42, label: 'PSG 23<br>+42 pts' },
  { psg: 24, pts: 56, label: 'PSG 24<br>+56 pts' },
  { psg: 25, pts: 70, label: 'PSG 25<br>+70 pts' },
  { psg: 26, pts: 84, label: 'PSG 26<br>+84 pts' },
  { psg: 27, pts: 98, label: 'PSG 27<br>+98 pts' },
];

const TOP_PSG = ROWS[ROWS.length - 1].psg; // 27 — rating-in limit
const EXTENDED_PSGS = [25, 26];            // rows newly extended to Step 14 under the new agreement
const PTS_VALUES = ROWS.map(r => r.pts);   // [0,14,28,42,56,70,84,98]
const UNLOCK_PTS = 70;                     // points needed to unlock continued step growth past Step 10

const DATA = {
  C: {
    20: [75311.16, 75400.32, 76202.28, 77006.40, 77095.44, 77220.36, 79430.16, 81288.00, 84088.20, 86478.60],
    21: [75400.32, 76202.28, 77006.40, 77095.44, 77220.36, 80196.48, 81288.00, 84928.08, 87371.76, 90279.00],
    22: [76202.28, 76291.32, 77095.44, 77220.36, 79842.24, 82947.72, 85745.64, 88245.12, 90780.24, 94970.52],
    23: [76291.32, 77095.44, 77220.36, 79842.24, 82588.92, 85801.32, 89102.76, 91653.36, 94328.88, 100005.12],
    24: [77095.44, 77220.36, 79842.24, 82588.92, 85801.32, 89156.16, 92582.40, 95240.04, 98505.84, 104872.44],
    25: [77220.36, 79842.24, 82125.72, 86837.16, 90207.72, 93740.76, 96166.68, 99147.36, 102999.00, 109851.36, 109851.36, 109851.36, 109851.36, 109851.36],
    26: [80196.48, 82517.76, 84928.08, 90299.04, 93812.04, 97335.48, 99931.44, 103411.08, 107137.44, 110619.48, 113385.00, 114723.24, 114723.24, 114723.24],
    27: [81980.88, 85646.76, 88280.76, 91304.28, 94271.64, 97335.48, 100498.92, 103765.08, 107137.44, 110619.48, 113385.00, 116219.52, 119125.08, 121947.72],
  },
  B: {
    20: [81586.08, 81682.80, 82552.32, 83422.44, 83519.16, 83654.16, 86050.80, 88061.52, 91095.48, 93685.68],
    21: [81682.80, 82552.32, 83422.44, 83519.16, 83654.16, 86882.40, 88061.52, 92004.00, 94652.16, 97802.88],
    22: [82552.32, 82649.04, 83519.16, 83654.16, 86495.40, 89858.52, 92893.32, 95599.08, 98343.72, 102886.20],
    23: [82649.04, 83519.16, 83654.16, 86495.40, 89472.12, 92951.52, 96527.40, 99291.24, 102190.44, 108336.84],
    24: [83519.16, 83654.16, 86495.40, 89472.12, 92951.52, 96584.52, 100296.24, 103175.64, 106713.36, 113613.72],
    25: [83654.16, 86495.40, 88969.56, 94072.20, 97725.36, 101552.40, 104181.00, 107409.12, 111584.04, 119005.80, 119005.80, 119005.80, 119005.80, 119005.80],
    26: [86882.40, 89394.72, 92004.00, 97822.08, 101629.56, 105446.76, 108259.32, 112028.52, 116065.68, 119837.76, 122833.68, 124282.92, 124282.92, 124282.92],
    27: [88814.88, 92784.00, 95638.32, 98913.00, 102127.68, 105446.76, 108873.84, 112412.28, 116065.68, 119837.76, 122833.68, 125904.48, 129052.20, 132110.64],
  },
  A: {
    20: [96396.96, 96510.96, 97538.88, 98566.44, 98680.80, 98840.28, 101672.76, 104047.68, 107632.56, 110693.28],
    21: [96510.96, 97538.88, 98566.44, 98680.80, 98840.28, 102654.24, 104047.68, 108706.32, 111835.32, 115557.36],
    22: [97538.88, 97653.24, 98680.80, 98840.28, 102197.76, 106171.44, 109757.16, 112954.32, 116196.84, 121564.08],
    23: [97653.24, 98680.80, 98840.28, 102197.76, 105714.60, 109825.20, 114050.40, 117316.44, 120741.72, 128003.88],
    24: [98680.80, 98840.28, 102197.76, 105714.60, 109825.20, 114118.80, 118503.24, 121906.32, 126085.68, 134238.36],
    25: [98840.28, 102197.76, 105120.72, 111150.24, 115466.16, 119987.76, 123094.20, 126907.92, 131840.40, 140610.36, 140610.36, 140610.36, 140610.36, 140610.36],
    26: [102654.24, 105623.28, 108706.32, 115580.16, 120079.32, 124532.16, 127912.80, 132366.12, 137073.00, 141527.88, 145066.08, 146844.72, 146844.72, 146844.72],
    27: [104937.96, 109577.52, 112999.80, 116815.80, 120612.36, 124532.16, 128579.52, 132758.28, 137073.00, 141527.88, 145066.08, 148692.72, 152410.08, 156094.08],
  },
  E: {
    20: [86385.24, 86487.72, 87408.24, 88329.60, 88432.08, 88575.00, 91112.64, 93241.44, 96454.32, 99196.80],
    21: [86487.72, 87408.24, 88329.60, 88432.08, 88575.00, 91992.84, 93241.44, 97416.00, 100219.68, 103555.68],
    22: [87408.24, 87510.84, 88432.08, 88575.00, 91583.52, 95144.76, 98357.76, 101222.64, 104128.68, 108938.16],
    23: [87510.84, 88432.08, 88575.00, 91583.52, 94735.32, 98419.08, 102204.96, 105131.40, 108201.60, 114709.56],
    24: [88432.08, 88575.00, 91583.52, 94735.32, 98419.08, 102266.64, 106195.92, 109244.88, 112990.56, 120296.76],
    25: [88575.00, 91583.52, 94203.36, 99606.00, 103473.96, 107526.00, 110309.52, 113727.00, 118147.80, 126006.60, 126006.60, 126006.60, 126006.60, 126006.60],
    26: [91992.84, 94653.24, 97416.00, 103576.44, 107607.84, 111649.56, 114627.72, 118618.56, 122892.96, 126887.04, 130059.24, 131593.80, 131593.80, 131593.80],
    27: [94039.32, 98241.96, 101263.56, 104731.44, 108135.12, 111649.56, 115278.12, 119024.76, 122892.96, 126887.04, 130059.24, 133310.64, 136643.52, 139882.44],
  }
};

const COL_LABELS = ['Yr 1','Yr 2','Yr 3','Yr 4','Yr 5','Yr 6','Yr 7','Yr 8','Yr 9','Yr 10','Yr 11','Yr 12','Yr 13','Yr 14'];

// ── PHASE-IN DATA (PSG 20-24, C/B/A bases only) ─────────────
// FULL_PLACEMENT sourced from UTLA's 4/11/2026 salary proposal document,
// which we determined represents full-placement (end-state) figures, not
// a bargaining proposal — confirmed by two independent spot-checks against
// UTLA's official calculator (PSG 20 Step 1 = $77,000, PSG 20 Step 10 =
// $102,684), both matching this document exactly.
//
// JAN_2027 is CE Credits Online's own calculation: min(full placement,
// "now" x 1.08), per UTLA's published rule that each phase closes the
// smaller of its percentage cap or the remaining gap to full placement.
// This formula reproduces both verified spot-checks exactly.
//
// PSG 25, 26, and 27's Full Placement figures were harder to source (no
// document covers all three rows directly) but turned out, after
// comprehensive verification, to share a SINGLE Full Placement track
// across ALL 14 columns. E Basis is derived (see comment below) since no
// full-placement source exists for it at all — the proposal document
// predates its addition.
const PHASED_PSGS = [20, 21, 22, 23, 24, 25, 26, 27];
const PHASED_BASES = ['C', 'B', 'A', 'E'];

const FULL_PLACEMENT = {
  C: {
    20: [77000.0, 79502.5, 82086.33, 84754.14, 87508.65, 90352.68, 93289.14, 96321.04, 99451.47, 102683.64],
    21: [78155.0, 80695.04, 83317.63, 86025.45, 88821.28, 91707.97, 94688.48, 97765.85, 100943.24, 104223.9],
    22: [79327.33, 81905.46, 84567.39, 87315.83, 90153.6, 93083.59, 96108.8, 99232.34, 102457.39, 105787.26],
    23: [80517.23, 83134.05, 85835.9, 88625.57, 91505.9, 94479.84, 97550.44, 100720.82, 103994.25, 107374.06],
    24: [81724.99, 84381.06, 87123.44, 89954.95, 92878.49, 95897.04, 99013.69, 102231.64, 105554.17, 108984.68],
  },
  // B Basis: derived from C Basis via LAUSD's own day-count ratio (221/204),
  // NOT the proposal document's own printed B Basis figures. The proposal
  // PDF's printed B Basis numbers carry a small (~$1.50-2) rounding
  // imprecision that doesn't match the live UTLA calculator; recomputing
  // from C Basis via the exact ratio matches the calculator precisely
  // across every cell checked (PSG 21 Steps 6/9/10, PSG 22 Step 5, PSG 23
  // Step 8). Confirmed the PDF itself is unchanged/current before making
  // this switch.
  B: {
    20: [83416.67, 86127.71, 88926.86, 91816.98, 94801.04, 97882.07, 101063.23, 104347.79, 107739.09, 111240.61],
    21: [84667.92, 87419.63, 90260.77, 93194.24, 96223.05, 99350.3, 102579.19, 105913.0, 109355.18, 112909.22],
    22: [85937.94, 88730.92, 91614.67, 94592.15, 97666.4, 100840.56, 104117.87, 107501.7, 110995.51, 114602.86],
    23: [87227.0, 90061.89, 92988.89, 96011.03, 99131.39, 102353.16, 105679.64, 109114.22, 112660.44, 116321.9],
    24: [88535.41, 91412.81, 94383.73, 97451.2, 100618.36, 103888.46, 107264.83, 110750.94, 114350.35, 118066.74],
  },
  A: {
    20: [98560.89, 101764.12, 105071.46, 108486.28, 112012.08, 115652.48, 119411.18, 123292.04, 127299.04, 131436.25],
    21: [100039.31, 103290.58, 106647.53, 110113.57, 113692.26, 117387.26, 121202.35, 125141.42, 129208.52, 133407.8],
    22: [101539.9, 104839.94, 108247.24, 111765.28, 115397.65, 119148.07, 123020.38, 127018.55, 131146.65, 135408.91],
    23: [103062.99, 106412.54, 109870.95, 113441.76, 117128.61, 120935.29, 124865.69, 128923.82, 133113.85, 137440.05],
    24: [104608.94, 108008.73, 111519.01, 115143.38, 118885.54, 122749.32, 126738.67, 130857.68, 135110.56, 139501.65],
  },
  // Derived: LAUSD's own annualization formula computes E Basis as C Basis
  // scaled by 234/204 days. Verified against real "Now" data across 9 cells
  // (max deviation 0.003%, consistent with independent per-basis rounding),
  // then applied to C's Full Placement to derive E's. Not independently
  // sourced from UTLA or LAUSD — a mathematical derivation from confirmed data.
  E: {
    20: [88323.53, 91194.04, 94157.85, 97217.98, 100377.57, 103639.84, 107008.13, 110485.9, 114076.69, 117784.18],
    21: [89648.38, 92561.96, 95570.22, 98676.25, 101883.23, 105194.44, 108613.26, 112143.18, 115787.83, 119550.94],
    22: [90993.11, 93950.38, 97003.77, 100156.39, 103411.48, 106772.35, 110242.45, 113825.33, 117524.65, 121344.21],
    23: [92358.0, 95359.65, 98458.83, 101658.74, 104962.65, 108373.93, 111896.09, 115532.71, 119287.52, 123164.36],
    24: [93743.37, 96790.04, 99935.71, 103183.62, 106537.09, 109999.55, 113574.53, 117265.7, 121076.84, 125011.84],
  },
};
// PSG 25, 26, and 27 share a SINGLE Full Placement track across ALL 14
// columns — not just Steps 11-14 as earlier confirmed, but the entire row.
// This was verified comprehensively: a full column-by-column check against
// UTLA's calculator showed PSG 25, 26, and 27's Full Placement figures are
// identical at every single step, matching the UTLA proposal document's
// PSG 25 row exactly throughout (including Step 10, which earlier
// caution had left unresolved).
//
// This also resolves the "veteran teacher" placement question: the
// individual, start-date-dependent adjustment affects WHICH cell a teacher
// who was previously at Step 10 currently occupies — it does not affect
// the dollar value of a given cell once you know your actual current
// placement. A teacher entering their real current PS Group and PS Level
// gets a fully
// deterministic, correct answer; the caveat is about confirming your
// current placement, not about the numbers themselves.
const SHARED_FULL_TRACK = {
  C: [82950.87, 85646.77, 88430.29, 91304.28, 94271.67, 97335.49, 100498.9, 103765.11, 107137.48, 110619.45, 113384.93, 116219.56, 119125.04, 122103.17],
  // B Basis: derived from C via the 221/204 ratio, same correction as above.
  B: [89863.44, 92784.0, 95799.48, 98912.97, 102127.64, 105446.78, 108873.81, 112412.2, 116065.6, 119837.74, 122833.67, 125904.52, 129052.13, 132278.43],
  A: [106178.07, 109628.86, 113191.8, 116870.53, 120668.82, 124590.56, 128639.75, 132820.55, 137137.21, 141594.17, 145134.03, 148762.38, 152481.44, 156293.47],
  E: [95149.53, 98241.88, 101434.74, 104731.38, 108135.15, 111649.53, 115278.15, 119024.68, 122892.99, 126887.02, 130059.18, 133310.67, 136643.43, 140059.52],
};
[25, 26, 27].forEach(psg => {
  ['C', 'B', 'A', 'E'].forEach(basis => {
    FULL_PLACEMENT[basis][psg] = SHARED_FULL_TRACK[basis].slice();
  });
});

const JAN_2027 = {
  C: {
    20: [77000.0, 79502.5, 82086.33, 83166.91, 83263.08, 83397.99, 85784.57, 87791.04, 90815.26, 93396.89],
    21: [78155.0, 80695.04, 83166.91, 83263.08, 83397.99, 86612.2, 87791.04, 91722.33, 94361.5, 97501.32],
    22: [79327.33, 81905.46, 83263.08, 83397.99, 86229.62, 89583.54, 92605.29, 95304.73, 98042.66, 102568.16],
    23: [80517.23, 83134.05, 83397.99, 86229.62, 89196.03, 92665.43, 96230.98, 98985.63, 101875.19, 107374.06],
    24: [81724.99, 83397.99, 86229.62, 89196.03, 92665.43, 95897.04, 99013.69, 102231.64, 105554.17, 108984.68],
  },
  B: {
    20: [83416.67, 86127.71, 88926.86, 90096.24, 90200.69, 90346.49, 92934.86, 95106.44, 98383.12, 101180.53],
    21: [84667.92, 87419.63, 90096.24, 90200.69, 90346.49, 93832.99, 95106.44, 99364.32, 102224.33, 105627.11],
    22: [85937.94, 88730.92, 90200.69, 90346.49, 93415.03, 97047.2, 100324.79, 103247.01, 106211.22, 111117.1],
    23: [87227.0, 90061.89, 90346.49, 93415.03, 96629.89, 100387.64, 104249.59, 107234.54, 110365.68, 116321.9],
    24: [88535.41, 90346.49, 93415.03, 96629.89, 100387.64, 103888.46, 107264.83, 110750.94, 114350.35, 118066.74],
  },
  A: {
    20: [98560.89, 101764.12, 105071.46, 106451.76, 106575.26, 106747.5, 109806.58, 112371.49, 116243.16, 119548.74],
    21: [100039.31, 103290.58, 106451.76, 106575.26, 106747.5, 110866.58, 112371.49, 117402.83, 120782.15, 124801.95],
    22: [101539.9, 104839.94, 106575.26, 106747.5, 110373.58, 114665.16, 118537.73, 121990.67, 125492.59, 131289.21],
    23: [103062.99, 106412.54, 106747.5, 110373.58, 114171.77, 118611.22, 123174.43, 126701.76, 130401.06, 137440.05],
    24: [104608.94, 106747.5, 110373.58, 114171.77, 118611.22, 122749.32, 126738.67, 130857.68, 135110.56, 139501.65],
  },
  E: {
    20: [88323.53, 91194.04, 94157.85, 95395.97, 95506.65, 95661.0, 98401.65, 100700.76, 104170.67, 107132.54],
    21: [89648.38, 92561.96, 95395.97, 95506.65, 95661.0, 99352.27, 100700.76, 105209.28, 108237.25, 111840.13],
    22: [90993.11, 93950.38, 95506.65, 95661.0, 98910.2, 102756.34, 106226.38, 109320.45, 112458.97, 117653.21],
    23: [92358.0, 95359.65, 95661.0, 98910.2, 102314.15, 106292.61, 110381.36, 113541.91, 116857.73, 123164.36],
    24: [93743.37, 95661.0, 98910.2, 102314.15, 106292.61, 109999.55, 113574.53, 117265.7, 121076.84, 125011.84],
  },
};
// PSG 25/26/27: Jan 2027 computed for all 14 columns, each row using its
// own "Now" value against the shared Full Placement track above.
[25, 26, 27].forEach(psg => {
  ['C', 'B', 'A', 'E'].forEach(basis => {
    const nowVals = DATA[basis][psg];
    const fullVals = FULL_PLACEMENT[basis][psg];
    const arr = [];
    for (let i = 0; i < 14; i++) {
      arr.push(Math.round(Math.min(fullVals[i], nowVals[i] * 1.08) * 100) / 100);
    }
    JAN_2027[basis][psg] = arr;
  });
});
const BASIS_LABELS = { C: 'C Basis (204 Days)', B: 'B Basis (221 Days)', A: 'A Basis (260 Days)', E: 'E Basis (234 Days)' };
const BASIS_DESC   = { C: 'C Basis (204 days)', B: 'B Basis (221 days)', A: 'A Basis (260 days)', E: 'E Basis (234 days)' };
const fmt = n => '$' + Math.round(n).toLocaleString();

let currentBasis = 'C';
let currentYears = 5;
let currentPtsIdx = 0;

// ── Table builder ──────────────────────────────────────────
function buildTable(basis) {
  const tbody = document.getElementById('table-body');
  if (!tbody) return;
  tbody.innerHTML = '';
  const showPhases = PHASED_BASES.includes(basis);

  ROWS.forEach(row => {
    const data = DATA[basis][row.psg];
    let lbl = row.label;
    if (row.psg === TOP_PSG) lbl += '<span class="row-top-tag">Rating-In Limit</span>';
    else if (EXTENDED_PSGS.includes(row.psg)) lbl += '<span class="row-extended-tag">Was Capped at Step 10</span>';

    const isPhased = showPhases && PHASED_PSGS.includes(row.psg);

    if (!isPhased) {
      const tr = document.createElement('tr');
      if (row.psg === TOP_PSG) tr.className = 'row-top';
      let html = `<td class="row-label">${lbl}</td><td class="phase-tag">Today</td>`;
      for (let c = 0; c < COL_LABELS.length; c++) {
        html += data[c] !== undefined
          ? `<td id="cell-${row.psg}-${c}">${fmt(data[c])}</td>`
          : `<td class="empty-cell" id="cell-${row.psg}-${c}">&mdash;</td>`;
      }
      tr.innerHTML = html;
      tbody.appendChild(tr);
    } else {
      const phases = [
        { key: 'now',  tag: 'Today',          cls: 'phase-now',  vals: data },
        { key: 'jan',  tag: 'Jan 2027',       cls: 'phase-jan',  vals: JAN_2027[basis][row.psg] },
        { key: 'full', tag: 'Full Placement', cls: 'phase-full', vals: FULL_PLACEMENT[basis][row.psg] },
      ];
      phases.forEach((phase, i) => {
        const tr = document.createElement('tr');
        tr.className = phase.cls;
        let html = '';
        if (i === 0) html += `<td class="row-label" rowspan="3">${lbl}</td>`;
        html += `<td class="phase-tag">${phase.tag}</td>`;
        for (let c = 0; c < COL_LABELS.length; c++) {
          const val = phase.vals[c];
          if (val === undefined) {
            html += `<td class="empty-cell">&mdash;</td>`;
          } else if (phase.key === 'now') {
            html += `<td id="cell-${row.psg}-${c}">${fmt(val)}</td>`;
          } else {
            html += `<td>${fmt(val)}</td>`;
          }
        }
        tr.innerHTML = html;
        tbody.appendChild(tr);
      });
    }
  });
  const lbl = document.getElementById('table-basis-label');
  if (lbl) lbl.textContent = BASIS_LABELS[basis];
}

// ── Diagram builder ────────────────────────────────────────
function buildDiagram(ptsIdx, yearsVal) {
  const colIdx  = Math.min(yearsVal - 1, COL_LABELS.length - 1);
  const selPsg  = ROWS[Math.min(ptsIdx, ROWS.length - 1)].psg;
  const maxStart = COL_LABELS.length - 4;
  const startCol = Math.max(0, Math.min(colIdx - 1, maxStart));
  const showCols = [startCol, startCol + 1, startCol + 2, startCol + 3];
  const grid = document.getElementById('diag-grid');
  if (!grid) return;

  let html = '<div class="diag-cell diag-header" style="border-radius:4px 0 0 0">Pts</div>';
  showCols.forEach((c, i) => {
    html += `<div class="diag-cell diag-header"${i === 3 ? ' style="border-radius:0 4px 0 0"' : ''}>${COL_LABELS[c] || COL_LABELS[COL_LABELS.length - 1]}</div>`;
  });

  ROWS.forEach(row => {
    const isTop = row.psg === TOP_PSG;
    const lbl = row.pts === 0 ? '0' : '+' + row.pts;
    html += `<div class="diag-cell ${isTop ? 'diag-row-top-label' : 'diag-row-label'}">${lbl}</div>`;
    showCols.forEach(c => {
      const d = DATA[currentBasis][row.psg];
      const isHl = (row.psg === selPsg && c === colIdx);
      const val = d[c];
      const cls = isHl ? 'diag-highlight' : (isTop ? 'diag-row-top-data' : 'diag-data');
      html += val !== undefined
        ? `<div class="diag-cell ${cls}">${fmt(val)}</div>`
        : `<div class="diag-cell" style="background:#eeebe4;color:#bbb">&mdash;</div>`;
    });
  });
  grid.innerHTML = html;
}

// ── Selection reminder (next to the full table) ─────────────
function updateSelectionNote(row, colLabel, pts, basis) {
  const el = document.getElementById('table-selection-note');
  if (!el) return;
  el.innerHTML = `Showing your selection: <strong>PSG ${row.psg}</strong> &middot; <strong>${colLabel}</strong> &middot; <strong>${pts} salary points</strong> &middot; <strong>${BASIS_DESC[basis]}</strong>`;
}

// ── Calculator ─────────────────────────────────────────────
function getRowFromPtsIdx(idx) { return ROWS[Math.min(idx, ROWS.length - 1)]; }

function updateCalc() {
  const basis   = currentBasis;
  const years   = currentYears;
  const ptsIdx  = currentPtsIdx;
  const pts     = PTS_VALUES[ptsIdx];
  const colIdx  = Math.min(years - 1, COL_LABELS.length - 1);
  const row     = getRowFromPtsIdx(ptsIdx);
  const data    = DATA[basis][row.psg];
  const cIdx    = Math.min(colIdx, data.length - 1);
  const salary  = data[cIdx];
  const colLabel = COL_LABELS[cIdx];
  const isMaxRow = row.psg === TOP_PSG;
  const isMaxCol = colIdx >= data.length - 1;

  buildTable(basis);
  const cell = document.getElementById(`cell-${row.psg}-${cIdx}`);
  if (cell && !cell.classList.contains('empty-cell')) cell.classList.add('highlight');
  buildDiagram(ptsIdx, years);
  updateSelectionNote(row, colLabel, pts, basis);

  let nextRowGain = null, nextRowPts = null;
  if (!isMaxRow) {
    const nr = ROWS[ptsIdx + 1];
    const nd = DATA[basis][nr.psg];
    const ns = nd[Math.min(colIdx, nd.length - 1)];
    nextRowGain = ns - salary;
    nextRowPts  = nr.pts - pts;
  }
  const nextColGain = data[cIdx + 1] ? data[cIdx + 1] - salary : null;
  const topData = DATA[basis][TOP_PSG];
  const maxSal  = topData[topData.length - 1];
  const roiGap  = salary < maxSal ? (maxSal - salary) * 10 : 0;

  let nextHTML = '';
  if (isMaxRow && isMaxCol) {
    nextHTML = `<div class="result-next"><div class="result-label">Status</div><div class="result-next-amount" style="font-size:1.1rem;color:#8aaa8c">Maximum placement reached</div><div class="result-next-detail">Career increment eligibility may still apply after 5 years at max step</div></div>`;
  } else if (nextRowGain !== null) {
    nextHTML = `<div class="result-next"><div class="result-label">Earn ${nextRowPts} more point${nextRowPts !== 1 ? 's' : ''} \u2192 gain</div><div class="result-next-amount">${fmt(nextRowGain)}/yr</div><div class="result-next-detail">Moving to PSG ${row.psg + 1} at your current step</div></div>`;
  } else if (isMaxRow) {
    nextHTML = `<div class="result-next"><div class="result-label">Next step advance \u2192 gain</div><div class="result-next-amount">${nextColGain ? fmt(nextColGain) + '/yr' : '\u2014'}</div><div class="result-next-detail">Seniority step, no salary points needed</div></div>`;
  }

  // Messaging reflects the new agreement: 70 points is the threshold that
  // unlocks continued step growth past Step 10 (previously 98 points).
  let warnHTML = '';
  if (isMaxRow) {
    warnHTML = `<div class="result-warning"><strong>\u2713 You\u2019re on Pay Scale Group ${TOP_PSG}, the highest-paying row on the table.</strong> Your salary now advances by seniority step each year automatically, all the way to Step 14.</div>`;
  } else if (pts < UNLOCK_PTS) {
    const ptsToUnlock = UNLOCK_PTS - pts;
    warnHTML = `<div class="result-warning"><strong>You\u2019re ${ptsToUnlock} salary point${ptsToUnlock !== 1 ? 's' : ''} from Pay Scale Group 25.</strong> Under the new agreement, reaching 70 points unlocks continued step growth all the way to Step 14. Previously, teachers needed 98 points just to keep advancing past Step 10. <a href="/collections/lausd" style="color:#7a4010;font-weight:700;">Browse courses \u2192</a></div>`;
  } else {
    const ptsToTop = ROWS[ROWS.length - 1].pts - pts;
    warnHTML = `<div class="result-warning"><strong>You\u2019ve already unlocked continued step growth to Step 14.</strong> Earning ${ptsToTop} more point${ptsToTop !== 1 ? 's' : ''} would move you to Pay Scale Group ${TOP_PSG} for an even higher ceiling. <a href="/collections/lausd" style="color:#7a4010;font-weight:700;">Browse courses \u2192</a></div>`;
  }

  const resultEl = document.getElementById('calc-result');
  if (!resultEl) return;
  resultEl.innerHTML = `
    <div class="result-main">
      <div class="result-salary">
        <div class="result-label">Your Salary \u00b7 ${BASIS_DESC[basis]}</div>
        <div class="result-amount">${fmt(salary)}</div>
        <div class="result-position">PSG ${row.psg} \u00b7 ${colLabel} \u00b7 ${pts} salary points</div>
      </div>
      ${nextHTML}
    </div>
    <div class="result-roi">
      <div class="roi-item"><div class="roi-label">Salary gap vs. max row</div><div class="roi-value">${roiGap > 0 ? fmt(roiGap) + ' over 10 yrs' : 'At or near maximum'}</div><div class="roi-sub">cost of staying on the same row for 10 years</div></div>
      <div class="roi-item"><div class="roi-label">${nextRowPts !== null ? nextRowPts : ROWS[1].pts} points from your next raise</div><div class="roi-value">${nextRowGain ? fmt(nextRowGain) + '/yr' : 'Max row reached'}</div><div class="roi-sub">annual increase if you move to the next row</div></div>
      <div class="roi-item"><div class="roi-label">Raise on your next anniversary</div><div class="roi-value">${nextColGain ? fmt(nextColGain) + '/yr' : '\u2014'}</div><div class="roi-sub">automatic step increase on your next service anniversary</div></div>
    </div>
    ${warnHTML}
  `;
}

// ── Basis sync ─────────────────────────────────────────────
// Named setCEBasis to avoid conflict with any Shopify theme functions
function setCEBasis(basis, btn) {
  currentBasis = basis;
  document.querySelectorAll('.basis-btn').forEach(b => b.classList.remove('active'));
  btn.classList.add('active');
  document.querySelectorAll('.tbasis-btn').forEach(b => {
    b.classList.toggle('active', b.dataset.tb === basis);
  });
  updateCalc();
}

function setTableBasis(basis, btn) {
  currentBasis = basis;
  document.querySelectorAll('.tbasis-btn').forEach(b => b.classList.remove('active'));
  btn.classList.add('active');
  document.querySelectorAll('.basis-btn').forEach(b => {
    b.classList.toggle('active', b.dataset.basis === basis);
  });
  updateCalc();
}

// ── Input sync ─────────────────────────────────────────────
// setYears/setPts are the single source of truth for calculator state.
// Any control (main calculator sliders/selects, or the table's quick-adjust
// selects) calls these directly; every matching control on the page is
// then kept in sync via the shared js-years-*/js-pts-* classes.
function setYears(val) {
  currentYears = parseInt(val);
  document.querySelectorAll('.js-years-slider').forEach(el => el.value = currentYears);
  document.querySelectorAll('.js-years-select').forEach(el => el.value = currentYears);
  const display = document.getElementById('years-display');
  if (display) display.textContent = currentYears >= 14 ? '14+ years' : currentYears + ' year' + (currentYears > 1 ? 's' : '');
  updateCalc();
}

function setPts(idx) {
  currentPtsIdx = parseInt(idx);
  document.querySelectorAll('.js-pts-slider').forEach(el => el.value = currentPtsIdx);
  document.querySelectorAll('.js-pts-select').forEach(el => el.value = currentPtsIdx);
  const display = document.getElementById('pts-display');
  if (display) display.textContent = PTS_VALUES[currentPtsIdx] + ' points';
  updateCalc();
}

// ── Init ───────────────────────────────────────────────────
document.addEventListener('DOMContentLoaded', function () {
  buildTable('C');
  updateCalc();
});